<template>
    <div class="panel is-flex-grow-3">
        <div class="is-sticky has-shadow">
            <!-- Reset resized columns — only once the user has customised widths,
                 so sub-grids stay clean until then. -->
            <div v-if="hasCustomColumnWidths" class="basetable-grid-actions">
                <a class="basetable-reset-widths" :title="$t('Reset column widths')" @click="resetColumnWidths">
                    <b-icon icon="arrow-collapse-horizontal" size="is-small"></b-icon>
                </a>
            </div>

            <!-- Action bar -->
            <!-- <div class="is-clearfix action-bar">
                <div class="is-pulled-right">
                    <div class="field is-grouped">
                        <slot name="actions"></slot>
                    </div>
                </div>
                <div>
                    <div class="field is-grouped">
                        <a v-if="showAdd" @click="addItem" :title="$t('Add')" class="button is-primary">
                            <b-icon icon="plus" size="is-small"></b-icon>
                            <span>{{ $t('Add') }}</span>
                        </a>
                        <h3 v-if="title" class="title is-3 m-0">{{ $t(title) }}</h3>
                    </div>
                </div>
            </div> -->

            <!-- Filter bar -->
            <div v-if="$slots['filter-bar']" class="filter-bar">
                <slot name="filter-bar"></slot>
            </div>
        </div>

        <!-- Loading (only for non-infinite mode) -->
        <b-loading v-if="!infiniteScroll" :model-value="loading" :is-full-page="false"></b-loading>

        <!-- Table -->
        <b-table
            v-resizable-cols="{ gridKey: resource }"
            :data="filteredRows"
            :key="tableKey"
            :loading="!infiniteScroll && loading"
            :hoverable="hoverable"
            :striped="striped"
            :default-sort="defaultSort"
            :default-sort-direction="defaultSortDirection"
            :mobile-cards="mobileCards"
            :backend-sorting="backendSorting"
            @sort="onSort"
        >
            <slot></slot>

            <!-- Default action column -->
            <b-table-column numeric width="100" v-if="showActions" v-slot="props">
                <slot name="row-actions" :row="props.row">
                    <b-button
                        :title="$t('Edit')"
                        icon-left="pencil"
                        size="is-small"
                        @click="editItem(props.row)"
                    ></b-button>
                    <b-button
                        :title="$t('Delete')"
                        type="is-danger"
                        icon-left="delete"
                        size="is-small"
                        @click="removeItem(props.row)"
                        class="ml-1"
                    ></b-button>
                </slot>
            </b-table-column>

            <!-- Empty state for non-infinite mode -->
            <template v-if="!infiniteScroll" #empty>
                <app-empty />
            </template>

            <!-- Infinite scroll footer -->
            <template v-if="infiniteScroll" #footer>
                <infinite-loading ref="infiniteLoadingRef" @infinite="onInfiniteLoad" :identifier="infiniteLoadingId">
                    <template #complete>
                        <app-empty v-if="rows.length === 0" />
                    </template>
                </infinite-loading>
            </template>
        </b-table>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCrud } from '@/composables/useCrud';
import { useApi } from '@/services/api';
import { useOrgStore } from '@/stores/org';
import { useGridPreferencesStore } from '@/stores/gridPreferences';

const props = defineProps({
    // API resource name
    resource: {
        type: String,
        required: true
    },
    // Form component for add/edit
    formComponent: {
        type: Object,
        default: null
    },
    // // Page title
    // title: {
    //     type: String,
    //     default: ''
    // },
    // Field to display as item label (for delete confirmation)
    labelField: {
        type: String,
        default: 'name'
    },
    // // Show add button
    // showAdd: {
    //     type: Boolean,
    //     default: true
    // },
    // Show action column
    showActions: {
        type: Boolean,
        default: true
    },
    // Default sort field
    defaultSort: {
        type: String,
        default: 'name'
    },
    // Default sort direction
    defaultSortDirection: {
        type: String,
        default: 'asc'
    },
    // Table options
    hoverable: {
        type: Boolean,
        default: true
    },
    striped: {
        type: Boolean,
        default: false
    },
    mobileCards: {
        type: Boolean,
        default: false
    },
    // Filter function (for client-side filtering)
    filterFn: {
        type: Function,
        default: null
    },
    // Enable infinite scroll (vs loading all data at once)
    infiniteScroll: {
        type: Boolean,
        default: false
    },
    // Enable backend sorting (requires infiniteScroll)
    backendSorting: {
        type: Boolean,
        default: false
    },
    // Number of columns (for infinite scroll footer colspan)
    columnCount: {
        type: Number,
        default: 5
    },
    // Filter fields to sync with route query
    filterFields: {
        type: Array,
        default: () => []
    },
    // Route name for list (for URL query sync)
    routeName: {
        type: String,
        default: ''
    },
    // Additional params to send with API requests
    additionalParams: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['added', 'edited', 'removed', 'loaded', 'sort']);

const route = useRoute();
const router = useRouter();
const orgStore = useOrgStore();
const gridPreferences = useGridPreferencesStore();

// Reset control appears only once the user has resized a column on this grid.
const hasCustomColumnWidths = computed(() => Object.keys(gridPreferences.getColumnWidths(props.resource)).length > 0);

// Clear saved widths; the resize directive watches the store and restores defaults.
function resetColumnWidths() {
    gridPreferences.resetColumnWidths(props.resource);
}

const tableKey = ref(0);

// Refs
const infiniteLoadingRef = ref(null);
const loading = ref(!props.infiniteScroll);

// Sorting state
const sortField = ref(props.defaultSort);
const sortOrder = ref(props.defaultSortDirection);

// Infinite scroll state
const page = ref(0);
const lastPage = ref(false);
const infiniteLoadingId = ref(Date.now());

// Build API URL
const apiUrl = computed(() => `${orgStore.api}/${props.resource}`);

// API service
const api = useApi(props.resource, { orgId: () => route.params.org });

// Use CRUD composable
const {
    rows,
    addItem: crudAdd,
    editItem: crudEdit,
    removeItem: crudRemove
} = useCrud({
    api,
    formComponent: props.formComponent,
    displayField: props.labelField,
    onAdded: row => {
        tableKey.value++;
        emit('added', row);
    },
    onEdited: row => {
        tableKey.value++;
        emit('edited', row);
    },
    onRemoved: row => {
        tableKey.value++;
        emit('removed', row);
    }
});

// Filtered rows (client-side filtering)
const filteredRows = computed(() => {
    if (props.filterFn) {
        return rows.value.filter(props.filterFn);
    }
    return rows.value;
});

// Get query params for API calls
function getQueryParams() {
    const params = {
        ...props.additionalParams
    };

    // Add filter fields from route query
    props.filterFields.forEach(field => {
        const value = route.query[field];
        if (value !== undefined && value !== '') {
            params[field] = value;
        }
    });

    // Add sorting params for backend sorting
    if (props.backendSorting) {
        params.sort_field = sortField.value;
        params.sort_order = sortOrder.value;
    }

    return params;
}

// Load all data at once (non-infinite mode)
async function loadData() {
    loading.value = true;
    try {
        const response = await axios.get(apiUrl.value, {
            params: getQueryParams()
        });
        rows.value = Array.isArray(response.data) ? response.data : response.data.data || [];
        emit('loaded', rows.value);
    } catch (error) {
        new AxiosError(error.response).manage();
    } finally {
        loading.value = false;
    }
}

// Handle infinite scroll loading
async function onInfiniteLoad($state) {
    page.value++;

    if (lastPage.value) {
        $state.complete();
        return;
    }

    if (loading.value) return;
    loading.value = true;

    try {
        const params = {
            page: page.value,
            ...getQueryParams()
        };

        const response = await api.index(params);
        const data = response.data.data || response.data;

        if (data && data.length > 0) {
            rows.value.push(...data);
            loading.value = false;
            if (data.length < response.data.per_page) {
                lastPage.value = true;
                $state.complete();
            } else {
                $state.loaded();
            }
        } else {
            lastPage.value = true;
            $state.complete();
        }
    } catch (error) {
        new AxiosError(error.response).manage();
        loading.value = false;
        $state.error();
    }
}

// Reset list for infinite scroll mode
function resetList() {
    loading.value = false;
    page.value = 0;
    lastPage.value = false;
    rows.value = [];
    infiniteLoadingId.value = Date.now();
}

// Handle sorting
function onSort(field, order) {
    sortField.value = field;
    sortOrder.value = order;
    emit('sort', { field, order });

    if (props.backendSorting && props.infiniteScroll) {
        // Update route query if routeName is provided
        if (props.routeName && router) {
            router.replace({
                name: props.routeName,
                query: {
                    ...route.query,
                    sort_field: field,
                    sort_order: order
                }
            });
        } else {
            // Just reset and reload
            resetList();
        }
    }
}

// Initialize sorting from route query
function initSortFromRoute() {
    if (route.query.sort_field) {
        sortField.value = route.query.sort_field;
    }
    if (route.query.sort_order) {
        sortOrder.value = route.query.sort_order;
    }
}

// Watch route query changes for infinite scroll mode
watch(
    () => route.query,
    () => {
        if (props.infiniteScroll) {
            initSortFromRoute();
            resetList();
        }
    },
    { deep: true }
);

// Load data on mount
onMounted(async () => {
    initSortFromRoute();
    if (!props.infiniteScroll) {
        await loadData();
    }
});

/**
 * `defaultData` is what a page already knows about the row being added — the
 * calendar a holiday is declared into, say. Dropping it here would make the
 * form open blank on a screen that is not blank.
 */
function addItem(defaultData = {}) {
    if (props.formComponent) {
        crudAdd(defaultData);
    }
}

function editItem(row) {
    if (props.formComponent) {
        crudEdit(row);
    }
}

function removeItem(row) {
    crudRemove(row);
}

// Expose for parent components
defineExpose({
    rows,
    loading,
    sortField,
    sortOrder,
    infiniteLoadingId,
    loadData,
    resetList,
    addItem,
    editItem,
    removeItem
});
</script>

<style scoped>
.basetable-grid-actions {
    display: flex;
    justify-content: flex-end;
    padding: 0.25rem 0.25rem 0;
}
.basetable-reset-widths {
    color: var(--bulma-text-weak);
    padding: 0.15rem 0.35rem;
    border-radius: var(--bulma-radius);
    line-height: 1;
}
.basetable-reset-widths:hover {
    color: var(--bulma-text);
    background: var(--bulma-background);
}
</style>
